import React, { useEffect, useReducer, useContext } from "react";
import OpenDrillDownView from "../../common/OpenDrillDownView";
import {
  WmProgressIndicator,
  WmProgressMonitor,
  WmProgressSlice,
} from "@watermarkinsights/ripple-react";
import "../../common/Insights.scss";
import { sendRequest } from "../../../common/http";
import { AppContext, AppContextInterface } from "../../../contexts/AppContext";
import translate from "../../../i18n/translate";
import { useIntl } from "react-intl";
import { openFlyout } from "../../../static/js/commons";
import ApiError from "@/features/common/elements/ApiError";
import { useFormatMessage } from "@/features/common/helper";

const popover = (amount: number, total: number) => {
  return `${amount}/${total}`;
};

interface OrganizationProgressData {
  totalOrgs: number;
  organizationMissionIncomplete: number;
  organizationMissionComplete: number;
  learningOutcomes: number;
  learningOutcomesIncomplete: number;
  otherOutcomes: number;
  otherOutcomesIncomplete: number;
  iloMappings: number;
  iloMappingsIncomplete: number;
}

interface Hierarchy {
  level: number;
  name: string;
  showStats: boolean;
  status: string;
  type: string;
  uuid: string;
  children: Hierarchy[];
}

interface OrganizationProgressAction {
  type: string;
  data: OrganizationProgressData;
  hierarchy: Hierarchy;
  segment: string;
  apiError: boolean;
}
const defaultHierarchy = {
  level: 0,
  name: "",
  showStats: false,
  status: "",
  type: "",
  uuid: "",
  children: [],
};

const defaultAction = {
  type: "",
  data: {
    totalOrgs: 0,
    organizationMissionIncomplete: 0,
    organizationMissionComplete: 0,
    learningOutcomes: 0,
    learningOutcomesIncomplete: 0,
    otherOutcomes: 0,
    otherOutcomesIncomplete: 0,
    iloMappings: 0,
    iloMappingsIncomplete: 0,
  },
  hierarchy: defaultHierarchy,
  segment: "",
  apiError: false,
};

interface OrganizationProgressState {
  drilldownType: string;
  drilldownStatus: string;
  isDrilldownOpen: boolean;
  hierarchy: Hierarchy;
  showTable: boolean;
  totalOrgs: number;
  organizationMissionIncomplete: number;
  organizationMissionComplete: number;
  learningOutcomes: number;
  learningOutcomesIncomplete: number;
  otherOutcomes: number;
  otherOutcomesIncomplete: number;
  iloMappings: number;
  iloMappingsIncomplete: number;
  apiError: boolean;
}

const reducer = (
  state: OrganizationProgressState,
  action: OrganizationProgressAction
) => {
  switch (action.type) {
    case "setMissionOutcome":
      return {
        ...state,
        totalOrgs: action.data.totalOrgs,
        organizationMissionIncomplete:
          action.data.organizationMissionIncomplete,
        organizationMissionComplete: action.data.organizationMissionComplete,
        learningOutcomes: action.data.learningOutcomes,
        learningOutcomesIncomplete: action.data.learningOutcomesIncomplete,
        otherOutcomes: action.data.otherOutcomes,
        otherOutcomesIncomplete: action.data.otherOutcomesIncomplete,
        iloMappings: action.data.iloMappings,
        iloMappingsIncomplete: action.data.iloMappingsIncomplete,
      };
    case "openDrilldown":
      return {
        ...state,
        isDrilldownOpen: true,
        drilldownType: action.segment,
      };
    case "closeDrilldown":
      return {
        ...state,
        isDrilldownOpen: false,
        showTable: false,
        hierarchy: defaultHierarchy,
      };
    case "setHierarchy":
      return {
        ...state,
        hierarchy: action.hierarchy,
        showTable: true,
      };
    case "setApiError":
      return { ...state, apiError: action.apiError };
    default:
      return state;
  }
};

export interface OrganizationProgressCardProps {
  onTabLoad: (tab: string) => void;
  onComponentLoad: (type: string) => void;
}

export default function OrganizationProgressCard({
  onTabLoad,
  onComponentLoad,
}: OrganizationProgressCardProps) {
  const { leads, outcomesTerminology } =
    useContext<AppContextInterface>(AppContext);
  const formatMessage = useFormatMessage();
  const initialState = {
    drilldownType: "organization",
    drilldownStatus: "any",
    isDrilldownOpen: false,
    hierarchy: defaultHierarchy,
    showTable: false,
    totalOrgs: 0,
    organizationMissionIncomplete: 0,
    organizationMissionComplete: 0,
    learningOutcomes: 0,
    learningOutcomesIncomplete: 0,
    otherOutcomes: 0,
    otherOutcomesIncomplete: 0,
    iloMappings: 0,
    iloMappingsIncomplete: 0,
    apiError: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const applyResult = (result: OrganizationProgressData) => {
      dispatch({
        ...defaultAction,
        type: "setMissionOutcome",
        data: result,
      });
      onTabLoad("organizations");
      onComponentLoad("organizations");
    };
    const onError = () => {
      dispatch({
        ...defaultAction,
        type: "setApiError",
        apiError: true,
      });
      onTabLoad("organizations");
      onComponentLoad("organizations");
    };
    sendRequest(
      { uri: "/mission_outcome/organizations", camelize: true },
      applyResult,
      onError
    );
  }, []);

  const getDrilldownHierarchy = (segment: string) => {
    openDrilldown(segment);
    const applyResult = (result: Hierarchy) => {
      dispatch({
        ...defaultAction,
        type: "setHierarchy",
        hierarchy: result,
      });
    };

    sendRequest(
      { uri: `/hierarchy?segment=${segment}`, camelize: true },
      applyResult
    );
  };

  const openDrilldownView = (segment: string) => {
    getDrilldownHierarchy(segment);
  };
  const openDrilldown = (segment: string) => {
    dispatch({
      ...defaultAction,
      type: "openDrilldown",
      segment: segment,
    });
  };
  const closeDrilldown = () => {
    dispatch({
      ...defaultAction,
      type: "closeDrilldown",
    });
  };

  const added = useIntl().formatMessage({
    id: "app.drilldown.added",
  });
  const notAdded = useIntl().formatMessage({
    id: "app.drilldown.notAdded",
  });
  const created = useIntl().formatMessage({
    id: "app.drilldown.created",
  });

  const notCreated = useIntl().formatMessage({
    id: "app.drilldown.notCreated",
  });

  const mapped = useIntl().formatMessage({
    id: "app.drilldown.mapped",
  });

  const notMapped = useIntl().formatMessage({
    id: "app.drilldown.notMapped",
  });
  const POPOVER_BUTTON_TEXT = useIntl().formatMessage({
    id: "app.orgProgressCard.progressDrilldown",
  });
  const DD_SUB_HEADER = formatMessage(
    { id: "app.orgProgressCard.missionOutcome" },
    { outcome_singular: outcomesTerminology?.outcomes?.singular || "..." }
  );
  const totalText = useIntl().formatMessage(
    { id: "app.orgProgressCard.orgTotal" },
    { totalOrgs: state.totalOrgs }
  );
  const organizationsWithoutILO = useIntl().formatMessage({
    id: "app.orgProgressCard.organizationsWithoutILO",
  });
  const organizationsWithILO = useIntl().formatMessage({
    id: "app.orgProgressCard.organizationsWithILO",
  });
  const organizationsWithoutSO = formatMessage(
    { id: "app.orgProgressCard.organizationsWithoutSO" },
    { success_outcome_plural_lowercase: outcomesTerminology?.successOutcomes?.plural?.toLowerCase() || "..." }
  );
  const organizationsWithSO = formatMessage(
    { id: "app.orgProgressCard.organizationsWithSO" },
    { success_outcome_plural_lowercase: outcomesTerminology?.successOutcomes?.plural?.toLowerCase() || "..." }
  );
  const orgWithoutLO = formatMessage(
    { id: "app.orgProgressCard.orgWithoutLO" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural?.toLowerCase() || "..." }
  );
  const orgWithLO = formatMessage(
    { id: "app.orgProgressCard.orgWithLO" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural?.toLowerCase() || "..." }
  );
  const ILOMapped = useIntl().formatMessage({
    id: "app.orgProgressCard.ILOMapped",
  });
  
  const successOutcomes = formatMessage(
    { id: "app.orgProgressCard.successOutcomes" },
    { success_outcome_plural: outcomesTerminology?.successOutcomes?.plural || "..." }
  );
  
  const learningOutcomes = formatMessage(
    { id: "app.orgProgressCard.learningOutcomes" },
    { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
  );
  
  const orgWithoutMission = useIntl().formatMessage({
    id: "app.orgProgressCard.orgWithoutMission",
  });
  const orgWithMission = useIntl().formatMessage({
    id: "app.orgProgressCard.orgWithMission",
  });
  const missionStatement = useIntl().formatMessage({
    id: "app.orgProgressCard.missionStatement",
  });

  const orgChart = () => {
    return (
      <WmProgressMonitor id="organization-progress" bar-labels-width="5rem">
        <WmProgressIndicator
          label={missionStatement}
          onWmProgressIndicatorButtonTriggered={() =>
            openDrilldownView("organization_mission")
          }
        >
          <WmProgressSlice
            legend={added}
            amount={state.organizationMissionComplete.toString()}
            popoverTitle={popover(
              state.organizationMissionComplete,
              state.totalOrgs
            )}
            popoverText={orgWithMission}
            popoverButtonText={POPOVER_BUTTON_TEXT}
          />
          <WmProgressSlice
            legend={notAdded}
            amount={state.organizationMissionIncomplete.toString()}
            popoverTitle={popover(
              state.organizationMissionIncomplete,
              state.totalOrgs
            )}
            popoverText={orgWithoutMission}
            popoverButtonText={POPOVER_BUTTON_TEXT}
          />
        </WmProgressIndicator>

        <WmProgressIndicator
          label={learningOutcomes}
          onWmProgressIndicatorButtonTriggered={() =>
            openDrilldownView("organization_learning_outcome")
          }
        >
          <WmProgressSlice
            legend={created}
            amount={state.learningOutcomes.toString()}
            popoverTitle={popover(state.learningOutcomes, state.totalOrgs)}
            popoverText={orgWithLO}
            popoverButtonText={POPOVER_BUTTON_TEXT}
          />
          <WmProgressSlice
            legend={notCreated}
            amount={state.learningOutcomesIncomplete.toString()}
            popoverTitle={popover(
              state.learningOutcomesIncomplete,
              state.totalOrgs
            )}
            popoverText={orgWithoutLO}
            popoverButtonText={POPOVER_BUTTON_TEXT}
          />
        </WmProgressIndicator>

        <WmProgressIndicator
          label={successOutcomes}
          onWmProgressIndicatorButtonTriggered={() =>
            openDrilldownView("organization_success_outcome")
          }
        >
          <WmProgressSlice
            legend={created}
            amount={state.otherOutcomes.toString()}
            popoverTitle={popover(state.otherOutcomes, state.totalOrgs)}
            popoverText={organizationsWithSO}
            popoverButtonText={POPOVER_BUTTON_TEXT}
          />
          <WmProgressSlice
            legend={notCreated}
            amount={state.otherOutcomesIncomplete.toString()}
            popoverTitle={popover(
              state.otherOutcomesIncomplete,
              state.totalOrgs
            )}
            popoverText={organizationsWithoutSO}
            popoverButtonText={POPOVER_BUTTON_TEXT}
          />
        </WmProgressIndicator>

        <WmProgressIndicator
          label={ILOMapped}
          onWmProgressIndicatorButtonTriggered={() =>
            openDrilldownView("organization_ilo_mapping")
          }
        >
          <WmProgressSlice
            legend={mapped}
            amount={state.iloMappings.toString()}
            popoverTitle={popover(state.iloMappings, state.totalOrgs)}
            popoverText={organizationsWithILO}
            popoverButtonText={POPOVER_BUTTON_TEXT}
          />
          <WmProgressSlice
            legend={notMapped}
            popoverTitle={popover(state.iloMappingsIncomplete, state.totalOrgs)}
            popoverText={organizationsWithoutILO}
            amount={state.iloMappingsIncomplete.toString()}
            popoverButtonText={POPOVER_BUTTON_TEXT}
          />
        </WmProgressIndicator>
      </WmProgressMonitor>
    );
  };
  return (
    <React.Fragment>
      {state.apiError ? (
        <ApiError containerClasses="api-error" />
      ) : (
        <div>
          <h4 className="title">
            {translate("app.orgProgressCard.orgProgress")}
          </h4>
          <p className="org totals">
            {translate("app.orgProgressCard.orgTotal", {
              totalOrgs: state.totalOrgs,
            })}
          </p>
          <div id="org-charts">{orgChart()}</div>
        </div>
      )}

      <OpenDrillDownView
        setIsDrilldownOpen={closeDrilldown}
        drilldownType={state.drilldownType}
        drilldownStatus={state.drilldownStatus}
        subHeader={DD_SUB_HEADER}
        total={totalText}
        showTable={state.showTable}
        completeHierarchy={state.hierarchy}
        leads={leads}
        isVisible={state.isDrilldownOpen}
        open={state.isDrilldownOpen}
        id="org-progress-drill-down-view"
      ></OpenDrillDownView>
    </React.Fragment>
  );
}
